import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axios";
import { API_BASE } from "../../utils/url";
import { FaArrowLeft } from "react-icons/fa";

// API_BASE from utils/url is normalized
const DEFAULT_AVATAR = "https://randomuser.me/api/portraits/men/32.jpg";

const UserProfilePage = () => {
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Form state
  const [form, setForm] = useState({
    name: localUser.name || "",
    email: localUser.email || "",
    contact_no: localUser.contact_no || "",
    password: "",
    profile_photo: null,
    profile_preview: localUser.profile_photo
      ? (localUser.profile_photo.startsWith("http")
          ? localUser.profile_photo
          : API_BASE + localUser.profile_photo.replace(/^\/+/, ''))
      : DEFAULT_AVATAR,
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Handle input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  // Handle image change
  const handleImage = e => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    setForm(f => ({
      ...f,
      profile_photo: file,
      profile_preview: URL.createObjectURL(file),
    }));
  };

  // Handle submit
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("contact_no", form.contact_no);
      if (form.password) formData.append("password", form.password);
      if (form.profile_photo instanceof File) formData.append("profile_photo", form.profile_photo);

      // Patch user
      const { data } = await api.put(
        `/users/${localUser.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Save updated user
      localStorage.setItem("user", JSON.stringify(data.user));
      setMsg("Profile updated!");
      setTimeout(() => {
        navigate("/account");
        window.location.reload(); // force update of header/profile details
      }, 900);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#fff",
      fontFamily: "Montserrat, Arial, sans-serif"
    }}>
      <div style={{ padding: "0 16px" }}>
        {/* Row: Back Button + Page Title */}
        <div style={{
          display: "flex", alignItems: "center", gap: 0,
          padding: "14px 0 10px 6px", marginBottom: 20
        }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/account')}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginRight: 10,
              display: "flex",
              alignItems: "center",
              cursor: "pointer"
            }}
            aria-label="Back"
          >
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none"
              stroke="#222" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div style={{
            fontWeight: 700,
            fontSize: 21,
            color: "#232323",
            letterSpacing: "0.2px",
            marginTop: 2
          }}>
            Update Profile
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: "center", marginTop: 24, marginBottom: 24 }}>
        <div>
          <label style={{ display: "block", marginBottom: 9 }}>
            <img
              src={form.profile_preview}
              alt="profile"
              style={{
                width: 72, height: 72, borderRadius: "50%",
                objectFit: "cover", margin: "0 auto", boxShadow: "0 2px 16px rgba(40,40,60,0.08)",
                cursor: "pointer"
              }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={handleImage}
              style={{ display: "none" }}
            />
            <span style={{
              fontSize: 13, color: "#222", display: "block", marginTop: 5, cursor: "pointer"
            }}>Change photo</span>
          </label>
        </div>
        <h2 style={{ fontWeight: 700, fontSize: 21, color: "#222", margin: 0 }}>
          Edit Profile
        </h2>
      </div>
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%", maxWidth: 380, margin: "0 auto",
          background: "#fff", borderRadius: 11,
          display: "flex", flexDirection: "column", gap: 14,
        }}
      >
        {msg && <div style={{
          background: "#eafbec",
          color: "#106b33",
          padding: "9px 11px",
          marginBottom: 7,
          borderRadius: 7,
          fontSize: 15,
          fontWeight: 500,
          textAlign: "center"
        }}>{msg}</div>}

        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          placeholder="Name"
          style={inputStyle}
        />

        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          required
          placeholder="Email"
          style={inputStyle}
        />

        <input
          type="text"
          name="contact_no"
          value={form.contact_no}
          onChange={handleChange}
          placeholder="Contact No"
          style={inputStyle}
        />

        <input
          type="password"
          name="password"
          value={form.password}
          onChange={handleChange}
          placeholder="New Password (leave blank to keep)"
          style={inputStyle}
          autoComplete="new-password"
        />

        <button
          type="submit"
          disabled={loading}
          style={{
            background: loading ? "#bbb" : "#232323",
            color: "#fff", border: "none",
            borderRadius: 7, fontWeight: 600,
            fontSize: 16, padding: "14px 0",
            cursor: loading ? "not-allowed" : "pointer",
            letterSpacing: 0.8,
            marginTop: 10
          }}
        >
          {loading ? "Updating..." : "Save Changes"}
        </button>
        <button
          type="button"
          style={{
            background: "#fafbfc", color: "#444",
            border: "1.5px solid #eee", borderRadius: 7,
            fontWeight: 500, padding: "10px 0", marginTop: 0,
            cursor: "pointer"
          }}
          onClick={() => navigate("/account")}
        >Cancel</button>
      </form>
      </div>
    </div>
  );
};

const inputStyle = {
  padding: "13px 11px",
  border: "1.5px solid #e1e1e1",
  borderRadius: 6,
  fontSize: 16,
  outline: "none",
  background: "#fafbfc",
  fontWeight: 500,
  color: "#191919",
};

export default UserProfilePage;
